import { CHANNEL_VALUES } from '../models/session.js';
import { INCOMING_COMMUNICATION_EVENT_NAME } from '../webhooks/trigger-catalog.js';
import { isEntryEdge } from './graph.js';
/**
 * Which node types work on which channels, and which channels a trigger can
 * offer. One declaration, read by the save-time rules and by the editor's
 * pickers, so what is refused on save is what the editor hides.
 */
/** Channels a node type can run on. Absent means every channel. */
const CHANNEL_BY_NODE_TYPE = {
    // A transfer moves a live call to a person. There is nothing to move on a
    // channel the caller is not holding open.
    transfer: ['CALL'],
};
/** The channels a node of this type can run on. */
export function channelsForNodeType(type) {
    return CHANNEL_BY_NODE_TYPE[type] ?? CHANNEL_VALUES;
}
/** Whether a node type runs on every one of these channels. */
export function nodeTypeRunsOn(type, channels) {
    const supported = channelsForNodeType(type);
    return channels.every(channel => supported.includes(channel));
}
/**
 * What a trigger says a graph can reach.
 *
 * An outbound trigger names the event field holding a phone number, an email
 * address, or both; a channel with no destination in the event cannot be
 * opened. Incoming names neither — whoever reaches the company brings their
 * own address — so every channel is open and the author picks.
 */
export function channelsForTriggerVariables(variables) {
    if (variables.eventName === INCOMING_COMMUNICATION_EVENT_NAME) {
        return CHANNEL_VALUES;
    }
    const channels = [];
    // A number is dialled or texted; the author picks which.
    if (variables.phoneVariable)
        channels.push('CALL', 'SMS');
    if (variables.emailVariable)
        channels.push('EMAIL');
    return channels;
}
export const CHANNEL_CHOICES = [
    { id: 'phone', label: 'Phone', channels: ['CALL'] },
    { id: 'email', label: 'Email', channels: ['EMAIL'] },
    { id: 'sms', label: 'SMS', channels: ['SMS'] },
];
/** What each channel is called on its own. */
const CHANNEL_LABELS = {
    CALL: 'Phone',
    EMAIL: 'Email',
    SMS: 'SMS',
};
/**
 * Whether these channels are one conversation: at most one channel. Nothing
 * picked yet is not a mix, which is the unfinished state, not a mistake.
 */
export function spansOneChannelChoice(channels) {
    const groups = CHANNEL_CHOICES.filter(choice => choice.channels.some(channel => channels.includes(channel)));
    return groups.length <= 1;
}
/** The choices a trigger can reach, one per channel it offers. */
export function channelChoicesFor(offered) {
    return CHANNEL_CHOICES.map(choice => {
        const channels = choice.channels.filter(channel => offered.includes(channel));
        return {
            ...choice,
            channels,
            label: channels.map(channel => CHANNEL_LABELS[channel]).join(', '),
        };
    }).filter(choice => choice.channels.length > 0);
}
/** Which variable type carries a destination on each channel. */
const VARIABLE_TYPE_BY_CHANNEL = {
    CALL: 'phone',
    SMS: 'phone',
    EMAIL: 'email',
};
/** The order an outbound picker lists its channels in. */
const OUTBOUND_CHANNEL_ORDER = ['CALL', 'SMS', 'EMAIL'];
/**
 * The trigger variables that can carry a destination, by type.
 *
 * Reads the typed variable list rather than the `phoneVariable` /
 * `emailVariable` pointers. Those pointers hold the first match only
 * (`deriveContactVariablesFromTypes`), so a trigger declaring two numbers can
 * reach one of them. The list is what the author declared.
 *
 * A variable with no type is read the way `normalizeTriggerVariables` reads
 * it: the one a pointer names takes that pointer's type, everything else is
 * text.
 */
export function destinationVariablesFor(trigger, type) {
    const pointer = type === 'phone' ? trigger.phoneVariable : trigger.emailVariable;
    const named = (trigger.variables ?? [])
        .filter(variable => (variable.type ?? inferredType(variable.name, trigger)) === type)
        .map(variable => variable.name);
    // A trigger with no variable list still has its pointer, which is what an
    // older row carries.
    if (named.length === 0 && pointer)
        return [pointer];
    return named;
}
/** The type a variable carries when the row predates typing. */
function inferredType(name, trigger) {
    if (name === trigger.phoneVariable)
        return 'phone';
    if (name === trigger.emailVariable)
        return 'email';
    return 'text';
}
/**
 * What a Start node can open on, one row per channel and destination.
 *
 * Outbound sends, so each row is one channel and one variable: a trigger
 * declaring two numbers offers a call and a text to each of them. The author
 * picks which number, which the trigger's own pointer cannot express.
 *
 * Inbound answers, so it keeps the grouped choice and names no variable.
 */
export function destinationChoicesFor(trigger) {
    if (!trigger)
        return [];
    if (trigger.eventName === INCOMING_COMMUNICATION_EVENT_NAME) {
        return channelChoicesFor(CHANNEL_VALUES).map(choice => ({
            ...choice,
            variable: null,
        }));
    }
    return OUTBOUND_CHANNEL_ORDER.flatMap((channel) => {
        const type = VARIABLE_TYPE_BY_CHANNEL[channel];
        const variables = destinationVariablesFor(trigger, type);
        if (variables.length === 0) {
            return [
                {
                    id: `${channel}:`,
                    label: CHANNEL_LABELS[channel],
                    channels: [channel],
                    variable: null,
                    disabledReason: `add ${type === 'phone' ? 'a phone' : 'an email'} variable to the trigger`,
                },
            ];
        }
        return variables.map(variable => ({
            id: `${channel}:${variable}`,
            label: `${CHANNEL_LABELS[channel]} — {${variable}}`,
            channels: [channel],
            variable,
        }));
    });
}
/**
 * The choice a Start node currently holds, or undefined when it holds none.
 * Matched on the stored channel and variable together, so a graph pointing at
 * a variable the trigger dropped reads as unset rather than as another row.
 */
export function pickedDestinationChoice(choices, start) {
    if (start.channels.length === 0)
        return undefined;
    return choices.find(choice => !choice.disabledReason &&
        (choice.variable ?? null) === (start.destinationVariable ?? null) &&
        choice.channels.length === start.channels.length &&
        choice.channels.every(channel => start.channels.includes(channel)));
}
/**
 * The Start nodes a node can be reached from, walking forward from each one.
 * A node's channels are whatever every Start that reaches it can offer, so
 * this is what decides whether its type is allowed.
 *
 * Written as reachability rather than as a graph-wide channel: a graph holds
 * one conversation today, so the two agree, but a graph holding several would
 * have no single answer.
 *
 * An edge that leaves no node runs from anywhere, so its target is reachable
 * from every Start — voicemail uses one. Skipping them left the target out of
 * the map, and the rules that read it passed over the node.
 */
export function startsReaching(graph) {
    const outgoing = new Map();
    // Edges that leave no node. The entry edge leads to a Start, not from one.
    const fromAnywhere = [];
    for (const edge of graph.edges) {
        if (edge.toNodeId == null)
            continue;
        if (edge.fromNodeId == null) {
            if (isEntryEdge(edge))
                continue;
            fromAnywhere.push(edge.toNodeId);
            continue;
        }
        const from = outgoing.get(edge.fromNodeId) ?? [];
        from.push(edge.toNodeId);
        outgoing.set(edge.fromNodeId, from);
    }
    const reached = new Map();
    for (const start of graph.nodes) {
        if (start.type !== 'start_conversation')
            continue;
        const queue = [start.id, ...fromAnywhere];
        const seen = new Set(queue);
        while (queue.length > 0) {
            const current = queue.shift();
            const starts = reached.get(current) ?? new Set();
            starts.add(start.id);
            reached.set(current, starts);
            for (const next of outgoing.get(current) ?? []) {
                if (seen.has(next))
                    continue;
                seen.add(next);
                queue.push(next);
            }
        }
    }
    return reached;
}
//# sourceMappingURL=channel-capabilities.js.map